'use client';

import { useState } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Calendar, Play } from 'lucide-react';
import { getDotcmsImageURL } from '@/app/utils/getDotcmsImageURL';
import { DemoCard } from '@/components/demos/DemoCard';
import { DemoModal } from '@/components/demos/DemoModal';
import { useDemoModal } from '@/components/demos/DemoModalProvider';

export function DemoDetail({ demo, otherDemos = [] }) {
    const thumbSrc = demo.thumbnail?.idPath ?? demo.thumbnail?.identifier ?? demo.thumbnail;

    return (
        <div className="bg-white text-blue-700">
            <Hero demo={demo} thumbSrc={thumbSrc} />
            {otherDemos.length > 0 && <MoreTours demos={otherDemos} />}
        </div>
    );
}

function Hero({ demo, thumbSrc }) {
    const [isTourOpen, setIsTourOpen] = useState(false);
    const { openScheduleModal } = useDemoModal();

    const canLaunch = Boolean(demo.framecode || demo.demoUrl);
    const handleLaunch = () => {
        if (demo.framecode) {
            setIsTourOpen(true);
        } else if (demo.demoUrl) {
            window.open(demo.demoUrl, '_blank', 'noopener,noreferrer');
        }
    };

    return (
        <section
            className="relative text-white"
            style={{
                background:
                    'radial-gradient(ellipse 70% 90% at 35% 45%, rgba(182,38,232,0.10) 0%, transparent 65%), #0C1231'
            }}>
            <div className="mx-auto max-w-6xl px-6 pb-20 pt-16">
                <div className="mx-auto max-w-3xl text-center">
                    <div className="flex items-center justify-center gap-3">
                        {demo.category && (
                            <span className="inline-flex items-center rounded-full border border-fuschia-600/50 bg-fuschia-600/20 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-white/80">
                                {demo.category}
                            </span>
                        )}
                        {demo.duration && (
                            <p className="text-sm font-medium uppercase tracking-wider text-fuschia-600">
                                {demo.duration}
                            </p>
                        )}
                    </div>
                    <h1 className="mt-4 text-4xl font-semibold tracking-tight sm:text-5xl">
                        {demo.title}
                    </h1>
                    {demo.description && (
                        <p className="text-lg mt-4 leading-relaxed text-white/70">
                            {demo.description}
                        </p>
                    )}
                </div>

                <div className="relative mt-10 aspect-[16/9] overflow-hidden rounded-3xl bg-blue-800 shadow-2xl">
                    {thumbSrc ? (
                        <Image
                            src={getDotcmsImageURL({ src: thumbSrc, width: 1600 })}
                            alt={demo.title}
                            fill
                            className="object-cover object-top"
                            sizes="(max-width: 1024px) 100vw, 1100px"
                            priority
                        />
                    ) : (
                        <div className="absolute inset-0 bg-blue-800" />
                    )}

                    {canLaunch && (
                        <>
                            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
                            <div className="absolute inset-0 flex items-center justify-center">
                                <button
                                    onClick={handleLaunch}
                                    className="inline-flex cursor-pointer items-center gap-2.5 whitespace-nowrap rounded-md bg-white px-7 py-3.5 text-base font-semibold text-gray-900 shadow-[0_4px_24px_rgba(0,0,0,0.25),0_1px_4px_rgba(0,0,0,0.15)] transition-all duration-200 hover:scale-[1.04] hover:shadow-[0_8px_32px_rgba(0,0,0,0.3)] active:scale-[0.98]">
                                    <Play className="size-4 fill-fuschia-700 text-fuschia-700" />
                                    <span>Launch Tour</span>
                                </button>
                            </div>
                        </>
                    )}
                </div>

                <div className="mt-10 flex flex-col items-center gap-3 text-center">
                    <p className="text-sm text-white/60">
                        Want a guided walkthrough on your own content?
                    </p>
                    <button
                        onClick={() => openScheduleModal('demo_detail')}
                        className="group inline-flex items-center gap-2.5 rounded-md bg-fuschia-700 px-7 py-3.5 text-base font-semibold text-white shadow-[0_8px_28px_rgba(182,38,232,0.35)] transition-all duration-200 hover:scale-[1.03] hover:bg-fuschia-600 hover:shadow-[0_12px_36px_rgba(182,38,232,0.45)] active:scale-[0.98]">
                        <Calendar className="size-4" />
                        Schedule a Demo
                    </button>
                </div>
            </div>

            {isTourOpen && (
                <DemoModal
                    frameCode={demo.framecode}
                    previewImage={
                        thumbSrc ? getDotcmsImageURL({ src: thumbSrc, width: 1600 }) : undefined
                    }
                    onClose={() => setIsTourOpen(false)}
                    onScheduleDemo={() => openScheduleModal('tour_modal')}
                />
            )}
        </section>
    );
}

function MoreTours({ demos }) {
    return (
        <section className="border-t border-blue-100 bg-white">
            <div className="mx-auto max-w-7xl px-6 py-16">
                <div className="mb-10 flex items-end justify-between">
                    <h2 className="text-3xl font-semibold tracking-tight">More Product Tours</h2>
                    <Link
                        href="/demo-center"
                        className="text-sm font-semibold text-primary transition hover:underline">
                        All Tours
                    </Link>
                </div>
                <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
                    {demos.map((d) => (
                        <DemoCard key={d.identifier} demo={d} />
                    ))}
                </div>
            </div>
        </section>
    );
}
